import { useState } from "react";
import { X, CaretLeft, CaretRight, Warning, Target } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { cn } from "@/lib/utils";
import { api } from "./api";

/* ── Constants ───────────────────────────────────────────────────────────── */

const PAGE_SIZE = 25;

const CATEGORY_CONFIG = {
  confident: {
    label:    "Knows well",
    emoji:    "🟢",
    emptyMsg: "No confident words yet — keep practising and they'll get there!",
  },
  struggling: {
    label:    "Struggling",
    emoji:    "🔴",
    emptyMsg: "No struggling words detected — great consistency so far!",
  },
  learning: {
    label:    "Still learning",
    emoji:    "📖",
    emptyMsg: "Words being actively worked through will appear here.",
  },
  never_seen: {
    label:    "Never seen",
    emoji:    "⚪",
    emptyMsg: "Every word has been encountered — brilliant progress!",
  },
};

const DIFFICULTY_STYLES = {
  easy:   "bg-teal/15 text-teal-ink",
  medium: "bg-warning/15 text-warning-ink",
  hard:   "bg-destructive/15 text-destructive",
};

const PERIOD_OPTIONS = [
  { value: "day",   label: "Today"      },
  { value: "week",  label: "This week"  },
  { value: "month", label: "This month" },
];

/* ── Accuracy pill ───────────────────────────────────────────────────────── */

function AccuracyPill({ accuracy }) {
  if (accuracy === null || accuracy === undefined) {
    return (
      <span className="w-10 text-center text-[11px] font-semibold text-muted-foreground tabular-nums">
        —
      </span>
    );
  }
  const cls =
    accuracy >= 70 ? "bg-teal/15 text-teal-ink"
    : accuracy >= 50 ? "bg-warning/15 text-warning-ink"
    : "bg-destructive/15 text-destructive";
  return (
    <span className={cn("w-10 rounded-full px-2 py-0.5 text-center text-[11px] font-extrabold tabular-nums", cls)}>
      {accuracy}%
    </span>
  );
}

/* ── Single word row ─────────────────────────────────────────────────────── */

function WordRow({ word }) {
  return (
    <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-muted/50">

      {/* Name + word type */}
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-black text-foreground">{word.word}</p>
        {word.word_type && (
          <p className="text-[10px] font-semibold capitalize text-muted-foreground">
            {word.word_type}
          </p>
        )}
      </div>

      {/* Difficulty badge — hidden on very small screens */}
      {word.difficulty && (
        <span className={cn(
          "hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize sm:inline-block",
          DIFFICULTY_STYLES[word.difficulty] ?? "bg-muted text-muted-foreground",
        )}>
          {word.difficulty}
        </span>
      )}

      {/* Stats row */}
      <div className="flex shrink-0 items-center gap-3 text-[11px] font-semibold text-muted-foreground">
        {/* Seen — hidden on mobile */}
        <span className="hidden w-8 text-right tabular-nums sm:block" title="Times seen in Learn mode">
          👁 {word.learn_views}
        </span>
        {/* Quizzed */}
        <span className="w-8 text-right tabular-nums" title="Times appeared in a quiz">
          📝 {word.quiz_appeared}
        </span>
        {/* Correct */}
        <span className="w-6 text-right font-extrabold tabular-nums text-teal-ink" title="Times answered correctly">
          ✓ {word.quiz_correct}
        </span>
        {/* Accuracy pill */}
        <AccuracyPill accuracy={word.accuracy} />
      </div>
    </div>
  );
}

/* ── Inline set-practice form ────────────────────────────────────────────── */

function SetPracticeForm({ child, words, onSuccess, onCancel }) {
  const maxWords = Math.min(words.length, 50);
  const isCapped = words.length > 50;

  // Build count chips: 10, 20, 30 where < max, then always the max
  const base   = [10, 20, 30].filter((n) => n < maxWords);
  const counts = [...new Set([...base, maxWords])];

  const defaultCount = counts.includes(20) ? 20 : counts[Math.floor(counts.length / 2)];

  const [count,  setCount]  = useState(defaultCount);
  const [period, setPeriod] = useState("week");
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  const handleConfirm = async () => {
    setSaving(true);
    setErr(null);
    try {
      const selectedIds = words.slice(0, count).map((w) => w.word_id);
      await api(`/children/${child.child_id}/target`, {
        method: "POST",
        body: { word_count: count, period, word_ids: selectedIds },
      });
      onSuccess();
    } catch (e) {
      setErr(e.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const chipBase = "rounded-full px-3.5 py-1.5 text-sm font-extrabold transition-colors";

  return (
    <div className="space-y-4 rounded-2xl border border-secondary/30 bg-secondary/10 p-4">
      <p className="font-display text-sm font-black text-foreground">Set practice target</p>

      {/* Word count */}
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          How many words?
        </p>
        <div className="flex flex-wrap gap-2">
          {counts.map((n) => {
            const isLast  = n === maxWords;
            const label   = isLast ? (isCapped ? `Max (${n})` : `All ${n}`) : `${n}`;
            return (
              <button
                key={n}
                onClick={() => setCount(n)}
                className={cn(
                  chipBase,
                  count === n
                    ? "bg-secondary text-white"
                    : "bg-muted text-muted-foreground hover:bg-muted/60",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Period */}
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Time period
        </p>
        <div className="flex flex-wrap gap-2">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={cn(
                chipBase,
                period === opt.value
                  ? "bg-secondary text-white"
                  : "bg-muted text-muted-foreground hover:bg-muted/60",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {err && <p className="text-xs font-bold text-destructive">{err}</p>}

      <div className="flex gap-2">
        <BloomButton
          size="sm"
          onClick={handleConfirm}
          disabled={saving}
          className="flex-1"
        >
          {saving ? "Creating…" : `Start ${count}-word target`}
        </BloomButton>
        <BloomButton size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </BloomButton>
      </div>
    </div>
  );
}

/* ── Main exported modal ─────────────────────────────────────────────────── */

/**
 * WordInsightsModal
 *
 * Props:
 *   child          — child object { child_id, first_name, … }
 *   category       — "confident" | "struggling" | "learning" | "never_seen"
 *   words          — array of word objects for this category (from /words/insights)
 *   onClose        — () => void
 *   onTargetCreated — () => void  called after a new target is successfully created
 *
 * Wrap with <AnimatePresence> in the parent for mount/unmount animation.
 */
export function WordInsightsModal({ child, category, words, onClose, onTargetCreated }) {
  const config = CATEGORY_CONFIG[category];

  const [page,           setPage]           = useState(0);
  const [showForm,       setShowForm]       = useState(false);
  const [showConflict,   setShowConflict]   = useState(false);
  const [checkingTarget, setCheckingTarget] = useState(false);

  const totalPages = Math.ceil(words.length / PAGE_SIZE);
  const pageWords  = words.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  /* Check for active target before revealing the Set Practice form */
  const handleSetPractice = async () => {
    setShowConflict(false);
    setCheckingTarget(true);
    try {
      const t = await api(`/children/${child.child_id}/target`);
      if (t.active) {
        setShowConflict(true);
      } else {
        setShowForm(true);
      }
    } catch {
      // On error assume no active target so the parent can still try
      setShowForm(true);
    } finally {
      setCheckingTarget(false);
    }
  };

  const handleFormSuccess = () => {
    onTargetCreated?.();
    // Parent's onTargetCreated callback closes the modal
  };

  /* "Set practice" button lives in the header — hide while form/conflict is open */
  const canSetPractice = !showForm && !showConflict && words.length > 0;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog — centred on all screen sizes: mobile, tablet, desktop.
          Centering is done via framer-motion x/y so it owns the full CSS
          transform and doesn't conflict with Tailwind translate classes.    */}
      <motion.div
        initial={{ opacity: 0, x: "-50%", y: "calc(-50% + 20px)" }}
        animate={{ opacity: 1, x: "-50%", y: "-50%"              }}
        exit={{    opacity: 0, x: "-50%", y: "calc(-50% + 20px)" }}
        transition={{ type: "spring", damping: 28, stiffness: 320 }}
        className={cn(
          "fixed z-50 flex flex-col overflow-hidden bg-background shadow-2xl rounded-3xl",
          "left-1/2 top-1/2",
          "w-[calc(100vw-2rem)] max-w-[620px] max-h-[85svh]",
        )}
      >

        {/* ── Sticky header ────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center gap-2.5 border-b border-border bg-background px-4 py-3.5">

          {/* Category icon + title + count */}
          <span className="text-xl leading-none">{config.emoji}</span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[15px] font-black leading-tight text-foreground">
              {config.label}
            </p>
            <p className="text-[10px] font-semibold text-muted-foreground">
              {words.length} word{words.length !== 1 ? "s" : ""} · {child.first_name}
            </p>
          </div>

          {/* Set practice button */}
          {canSetPractice && (
            <BloomButton
              size="sm"
              leftIcon={Target}
              onClick={handleSetPractice}
              disabled={checkingTarget}
              className="shrink-0"
            >
              {checkingTarget ? (
                "Checking…"
              ) : (
                <>
                  <span className="hidden sm:inline">Set practice</span>
                  <span className="sm:hidden">Practice</span>
                </>
              )}
            </BloomButton>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted/60"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* ── Scrollable body ───────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-2">

          {/* Active-target conflict warning */}
          {showConflict && (
            <div className="mx-1 mb-3 mt-2 flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning/10 p-3.5">
              <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-warning-ink" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-extrabold text-foreground">
                  {child.first_name} already has an active target
                </p>
                <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
                  End the current target first, then come back to set a new one from here.
                </p>
                <button
                  onClick={onClose}
                  className="mt-2 text-xs font-extrabold text-primary-strong hover:underline"
                >
                  View current target →
                </button>
              </div>
              <button
                onClick={() => setShowConflict(false)}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Dismiss"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          )}

          {/* Inline set-practice form */}
          {showForm && (
            <div className="mx-1 mb-3 mt-2">
              <SetPracticeForm
                child={child}
                words={words}
                onSuccess={handleFormSuccess}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}

          {/* Column headers */}
          {words.length > 0 && (
            <div className="flex items-center gap-3 px-3 pb-1 pt-2">
              <p className="flex-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Word
              </p>
              <div className="flex shrink-0 items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <span className="hidden w-8 text-right sm:block">Seen</span>
                <span className="w-8 text-right">Quizzed</span>
                <span className="w-6 text-right">✓</span>
                <span className="w-10 text-center">Score</span>
              </div>
            </div>
          )}

          {/* Word list / empty state */}
          {words.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <span className="text-4xl">{config.emoji}</span>
              <p className="mt-3 max-w-xs text-sm font-semibold text-muted-foreground">
                {config.emptyMsg}
              </p>
            </div>
          ) : (
            <div className="space-y-0.5 pb-2">
              {pageWords.map((word) => (
                <WordRow key={word.word_id} word={word} />
              ))}
            </div>
          )}
        </div>

        {/* ── Pagination ────────────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="flex shrink-0 items-center justify-between border-t border-border bg-background px-4 py-3">
            <button
              onClick={() => { setPage((p) => Math.max(0, p - 1)); }}
              disabled={page === 0}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-extrabold text-primary-strong transition-colors hover:bg-muted disabled:opacity-30"
            >
              <CaretLeft size={11} weight="bold" /> Prev
            </button>

            <p className="text-xs font-bold text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>

            <button
              onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); }}
              disabled={page === totalPages - 1}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-extrabold text-primary-strong transition-colors hover:bg-muted disabled:opacity-30"
            >
              Next <CaretRight size={11} weight="bold" />
            </button>
          </div>
        )}
      </motion.div>
    </>
  );
}